import React, { useEffect, useMemo, useState } from 'react';
import { TableColumn } from 'react-data-table-component';
import { z } from 'zod';
import { Link } from 'react-router-dom';
import ErrorBoundary from 'components/ErrorBoundary';
import Table from 'components/Table';
import Wrapper from 'components/Wrapper';
import SearchImage from 'assets/Images/Common/search.png';
import { tableSort } from 'utils/common';
import { getFilterSupportUsers, loginAsUser } from 'services/apiHandlers/SupportUsers';
import SupportUsersHeader from './SuppportUsersHeader';
import { LoginResponse } from 'services/Authentication';
import { Toastify } from 'App';
import UserImg from 'assets/Images/svg-images/user.svg';
import Key from 'assets/Images/Common/key.gif';
// Define the schema for support user
const supportUserSchema = z.object({
    id: z.number(),
    name: z.string(),
    owner: z.object({
        name: z.string(),
        email: z.string(),
    }),
    company: z.object({
        name: z.string(),
    }),
    quota: z.object({
        total: z.string(),
        used: z.string(),
    }),
    resource: z.object({
        status: z.string(),
        pending: z.boolean(),
        failed: z.boolean(),
    }),
    is_owner: z.boolean(),
    is_current_account: z.boolean(),
    is_current_user_account: z.boolean(),
    usable: z.boolean(),
    actions: z.object({
        can_edit: z.boolean(),
        can_use: z.boolean(),
    }),
});

const SupportUserResponseSchema = z.object({
    data: z.object({
        total_count: z.number(),
        search_term: z.string().optional(),
        current_page: z.number(),
        per_page: z.number(),
        accounts: z.array(supportUserSchema),
    }),
    status: z.number(),
});

type SupportUserResponse = z.infer<typeof SupportUserResponseSchema>;
type SupportUser = z.infer<typeof supportUserSchema>;

const AllSupportUsers = () => {
    const [loading, setLoading] = useState<boolean>(true);
    const [filteredUsers, setFilteredUsers] = useState<Array<SupportUser>>([]);
    const [filteredEntries, setFilteredEntries] = useState<number>(0);
    const [filterValue, setFilterValue] = useState<string>('');
    const [isFirstRender, setIsFirstRender] = useState<boolean>(true);

    const getFilterData = async (preventLoading?: boolean) => {
        if (!preventLoading) {
            setLoading(true);
        }
        try {
            const response = (await getFilterSupportUsers(
                filterValue
            )) as SupportUserResponse;
            if (response.status === 200) {
                setFilteredUsers(response?.data?.accounts);
                setFilteredEntries(response?.data?.total_count);
            }
        } catch (error) {
            console.error('Error fetching filtered support users:', error);
        } finally {
            setLoading(false);
        }
    };

    const nameSort = (rowA: SupportUser, rowB: SupportUser) => {
        return tableSort(rowA.name, rowB.name);
    };

    const loginInAsHandler = async (row: SupportUser) => {
        try {
            const loginResponse = (await loginAsUser(row.id)) as LoginResponse;
            if (loginResponse) {
                window.location.href = '/';
            } else {
                Toastify('Wrong Credentials', 'error', 'login1');
            }
        } catch (error) {
            console.error('Error logging in as user:', error);
            Toastify('Failed to login as user', 'error', 'login-error');
        }
    };

    const renderActionCell = (row: SupportUser) => {
        if (row.is_current_account) {
            return (
                <div className="flex items-center gap-1">
                    <i className="fa fa-key"></i>
                    <span>Using</span>
                    {row.is_current_user_account && (
                        <span className="text-gray-500">(Yours)</span>
                    )}
                </div>
            );
        }
        
        if (row.usable && row.actions.can_use) {
            return (
                <button 
                    type='button' 
                    className="flex items-center gap-1" 
                    onClick={() => loginInAsHandler(row)}
                    title="Use This Account"
                >
                    <span>
                        <img
                            className={'w-4 h-4 group-hover:invert group-hover:brightness-0 dark:brightness-0 dark:invert dark:group-hover:invert-0 dark:group-hover:brightness-100'}
                            src={UserImg}
                            alt="Box"
                        />
                    </span>
                    <span className='hover:underline'>Use</span>
                    {row.is_current_user_account && (
                        <span className="text-gray-500">(Yours)</span>
                    )}
                </button>
            );
        }
        
        if (row.resource.pending) {
            return (
                <div className="flex items-center gap-1 text-gray-400">
                    <img src="/assets/indicator.gif" alt="Processing" />
                    <span className="subtle">processing</span>
                </div>
            );
        }
        
        if (row.resource.failed) {
            return (
                <div className="flex items-center gap-1 text-gray-400">
                    <img src="/assets/silk/exclamation.gif" alt="Failed" />
                </div>
            );
        }
        
        return (
            <div className="flex items-center gap-1 text-gray-400">
                <img src="/assets/silk/door.gif" alt="Door" />
            </div>
        );
    };

    const columns: TableColumn<SupportUser>[] = useMemo(
        () => [
            {
                cell: (row) => (
                    <div className="dark:text-white">
                        {row.is_owner && <img className='ml-2' src={Key} alt='key'></img>}
                    </div>
                ),
                width: '5%',
            },
            {
                name: <div className='px-3'>Owner</div>,
                cell: (row, index) => (
                    <div className="dark:text-white">
                        <Link
                            data-pw={`${index === 0 ? 'support-user-name' : ''}`}
                            className="cursor-pointer px-3 font-medium text-[#495057] mt-1 flex flex-col dark:text-white"
                            to={`/support-users/${row.id}`}
                        >
                            {`${row.name} ${row.name !== row.owner.name ? `(${row.owner.name})` : ''}`}
                            <span className='text-xs text-gray-500'>{row.owner.email}</span>
                        </Link>
                    </div>
                ),
                sortable: true,
                sortFunction: nameSort,
            },
            {
                name: 'Company',
                cell: (row) => (
                    <div className="dark:text-white">
                        {row.company.name}
                    </div>
                ),
            },
            {
                name: 'Quota',
                cell: (row) => (
                    <div className="dark:text-white">
                        {row.quota.total}
                    </div>
                ),
            },
            {
                name: 'Used',
                cell: (row) => (
                    <div className="dark:text-white">
                        {row.quota.used}
                    </div>
                ),
            },
            {
                name: 'Actions',
                cell: (row) => (
                    <div className="dark:text-white flex items-center gap-4">
                        {renderActionCell(row)}
                    </div>
                ),
            },
        ],
        []
    );

    useEffect(() => {
        if(isFirstRender) return;
        const timeoutId = setTimeout(() => {
            getFilterData(false);
        }, 1000);

        return () => clearTimeout(timeoutId);
    }, [filterValue]);

    useEffect(() => {
        getFilterData();
        setIsFirstRender(false);
    }, []);

    return (
        <ErrorBoundary>
            <SupportUsersHeader />
            <div className="relative mx-4 my-8 min-h-[72vh]">
                <Wrapper>
                    <>
                        <div className="flex justify-between items-center px-5 border-b dark:border-[#fff] border-[#ced4da] py-[18px]">
                            <p className='text-base text-[#495057] font-medium dark:text-[#CED4DA]'>
                                Support Users
                            </p>
                            <div className="relative md:w-96 sm:w-80 flex items-center py-2 px-4 rounded-md border-gray-200 bg-[#F3F3F9] dark:bg-[#41464E]">
                                <input
                                    className="text-11 bg-[#F3F3F9] font-medium placeholder:text-gray-500 leading-3 text-gray-500 w-full pl-6 h-6 focus:outline-none dark:text-white dark:placeholder:text-white dark:bg-[#41464E]"
                                    type="text"
                                    name="search"
                                    placeholder="Search support users"
                                    value={filterValue}
                                    onChange={(e) => setFilterValue(e.target.value)}
                                />
                                <img
                                    className="absolute dark:invert dark:brightness-0"
                                    src={SearchImage}
                                    alt="Search"
                                />
                            </div>
                        </div>
                        <Table
                            className="scrollbar-hide"
                            data={filteredUsers}
                            columns={columns}
                            progressPending={loading}
                            pagination
                            paginationTotalRows={filteredEntries}
                        />
                    </>
                </Wrapper>
            </div>
        </ErrorBoundary>
    );
};

export default AllSupportUsers;